import type { ComponentType, JSX } from 'react';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { Text } from '@change/ds-core-ui';

import { FeatureList } from './components/FeatureList';
import { DecisionMakersImage, MediaAmplificationImage, SupportersNetworkImage } from './components/SectionImages';

const FILL_DURATION_MS = 8000;

type SectionImageProps = { className?: string };

type ReachMoreSection = {
	id: string;
	title: string;
	body: string;
	Image: ComponentType<SectionImageProps>;
};

const SECTIONS: readonly ReachMoreSection[] = [
	{
		id: 'supporters',
		title: 'Access to 200+ million activists worldwide',
		body: 'Change.org introduces your petition to people who care deeply about your issue, helping you find new supporters and grow your community beyond your own list.',
		Image: SupportersNetworkImage,
	},
	{
		id: 'media',
		title: 'Maximize your reach with our media network',
		body: 'Our team works with journalists and outlets to help your campaign gain the attention it needs to drive momentum.',
		Image: MediaAmplificationImage,
	},
	{
		id: 'decision-makers',
		title: 'Influence those who can deliver results',
		body: "Tag decision makers directly on your petition and benefit from our team's efforts to engage leaders and institutions—so your supporters' voices reach the right targets.",
		Image: DecisionMakersImage,
	},
];

export function ReachMore(): JSX.Element {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [animationReady, setAnimationReady] = useState(false);
	const onSelect = useCallback((index: number) => setSelectedIndex(index), []);

	// Start bar animation and auto-advance only after the user has scrolled (passive listener)
	useEffect(() => {
		if ((process.env.NODE_ENV as string) === 'test') return undefined;
		const onScroll = () => setAnimationReady(true);
		window.addEventListener('scroll', onScroll, { passive: true, once: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	// Desktop: auto-advance after bar fill duration (bar uses CSS keyframes in FeatureList; no-op in test)
	useLayoutEffect(() => {
		if ((process.env.NODE_ENV as string) === 'test' || !animationReady) return undefined;
		const timeout = setTimeout(() => setSelectedIndex((i) => (i + 1) % SECTIONS.length), FILL_DURATION_MS);
		return () => clearTimeout(timeout);
	}, [selectedIndex, animationReady]);

	const sectionsForList = SECTIONS.map(({ id, title, body }) => ({ id, title, body }));
	const SelectedImage = SECTIONS[selectedIndex].Image;

	return (
		<div className="bg-background-raised md:bg-background-base py-8">
			<div className="container pt-6">
				<div className="mb-10 flex flex-col items-center gap-4 text-center md:mb-18">
					<Text as="h2" variant="heading" size="small" className="md:text-size-heading sm:mb-4">
						Reach more supporters. Create more impact.
					</Text>
					<Text
						as="p"
						variant="body"
						size="large"
						className="text-size-body md:text-size-body-xl max-w-sm md:max-w-3xl"
					>
						Change.org connects nonprofits with millions of people ready to take action, helping turn campaigns into
						real change.
					</Text>
				</div>

				{/* Desktop: left = stacked descriptions, right = selected image */}
				<div className="hidden flex-col gap-8 md:flex md:flex-row md:gap-12">
					<div className="flex-1">
						<FeatureList
							sections={sectionsForList}
							selectedIndex={selectedIndex}
							onSelect={onSelect}
							fillDurationMs={FILL_DURATION_MS}
							enableBarAnimation={animationReady}
						/>
					</div>
					<div className="flex flex-1 items-center">
						<div className="h-96 w-full overflow-hidden rounded-xl">
							<SelectedImage className="h-full w-full rounded-xl object-cover object-center" />
						</div>
					</div>
				</div>

				{/* Mobile: stacked cards, each with image + title + body */}
				<div className="flex flex-col gap-8 md:hidden">
					{SECTIONS.map((section) => {
						const SectionImage = section.Image;
						return (
							<article key={section.id} className="bg-background-base overflow-hidden rounded-xl shadow-sm">
								<div className="p-4">
									<SectionImage className="mt-4 mb-8 w-full rounded-xl" />
									<Text as="h3" variant="body" size="large" fontWeight="bold" className="mb-2">
										{section.title}
									</Text>
									<Text variant="body" size="medium" className="text-typography-weak">
										{section.body}
									</Text>
								</div>
							</article>
						);
					})}
				</div>
			</div>
		</div>
	);
}
